/**
 * SS - Collection Carousel
 * Native scroll-snap carousel with:
 *  - a scroll progress bar you can click / drag to scrub
 *  - click-and-drag panning on desktop (pointer devices with a fine pointer)
 */
class CollectionCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.collection-carousel__track');
    this.progress = this.querySelector('.collection-carousel__progress');
    this.bar = this.querySelector('.collection-carousel__progress-bar');
    if (!this.track) return;

    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    window.addEventListener('resize', this.update);

    this.initTrackDrag();
    this.initProgressScrub();

    requestAnimationFrame(this.update);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.update);
  }

  onScroll() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      this.update();
    });
  }

  update() {
    if (!this.bar) return;
    const { scrollWidth, clientWidth, scrollLeft } = this.track;
    const scrollable = scrollWidth - clientWidth;

    if (scrollable <= 1) {
      this.bar.style.setProperty('--cc-progress-width', '100%');
      this.bar.style.setProperty('--cc-progress-x', '0%');
      return;
    }

    const visibleFraction = clientWidth / scrollWidth;
    const scrolledFraction = scrollLeft / scrollable;
    const travel = (1 - visibleFraction) * scrolledFraction;

    this.bar.style.setProperty('--cc-progress-width', `${(visibleFraction * 100).toFixed(2)}%`);
    this.bar.style.setProperty('--cc-progress-x', `${((travel / visibleFraction) * 100).toFixed(2)}%`);
  }

  /* Click-and-drag panning — pointer-based, only for fine pointers (mouse/
     trackpad). Touch keeps native momentum scrolling. */
  initTrackDrag() {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = 0;

    const onDown = (e) => {
      if (!fine.matches || e.button !== 0) return;
      dragging = true;
      moved = 0;
      startX = e.clientX;
      startScroll = this.track.scrollLeft;
      this.track.setPointerCapture(e.pointerId);
    };

    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      moved += Math.abs(e.movementX || 0);
      // Only enter "dragging" once past a small threshold, so plain clicks
      // still open the collection link.
      if (moved > 4 && !this.classList.contains('is-dragging')) {
        this.classList.add('is-dragging');
      }
      this.track.scrollLeft = startScroll - dx;
    };

    const onUp = (e) => {
      if (!dragging) return;
      dragging = false;
      try {
        this.track.releasePointerCapture(e.pointerId);
      } catch (_) {}
      // Release next tick so the click that follows a real drag is swallowed.
      if (this.classList.contains('is-dragging')) {
        requestAnimationFrame(() => this.classList.remove('is-dragging'));
      }
    };

    this.track.addEventListener('pointerdown', onDown);
    this.track.addEventListener('pointermove', onMove);
    this.track.addEventListener('pointerup', onUp);
    this.track.addEventListener('pointercancel', onUp);
    // Swallow the click fired at the end of a drag.
    this.track.addEventListener(
      'click',
      (e) => {
        if (this.classList.contains('is-dragging')) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  /* Click / drag the progress rail to scrub the carousel. */
  initProgressScrub() {
    if (!this.progress) return;
    let scrubbing = false;

    const seek = (clientX) => {
      const rect = this.progress.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const scrollable = this.track.scrollWidth - this.track.clientWidth;
      this.track.scrollLeft = ratio * scrollable;
    };

    this.progress.addEventListener('pointerdown', (e) => {
      scrubbing = true;
      this.progress.classList.add('is-scrubbing');
      this.progress.setPointerCapture(e.pointerId);
      seek(e.clientX);
    });
    this.progress.addEventListener('pointermove', (e) => {
      if (scrubbing) seek(e.clientX);
    });
    const end = (e) => {
      if (!scrubbing) return;
      scrubbing = false;
      this.progress.classList.remove('is-scrubbing');
      try {
        this.progress.releasePointerCapture(e.pointerId);
      } catch (_) {}
    };
    this.progress.addEventListener('pointerup', end);
    this.progress.addEventListener('pointercancel', end);
  }
}

if (!customElements.get('collection-carousel')) {
  customElements.define('collection-carousel', CollectionCarousel);
}
